import React, { useState, useEffect, useRef, useCallback } from 'react';
import confetti from 'canvas-confetti';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Bell sound for new sales
const playBellSound = () => {
  try {
    const audioContext = new (window.AudioContext || window.webkitAudioContext)();
    const oscillator = audioContext.createOscillator();
    const gainNode = audioContext.createGain();
    
    oscillator.connect(gainNode);
    gainNode.connect(audioContext.destination);
    
    oscillator.frequency.value = 830;
    oscillator.type = 'sine';
    
    gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
    gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.8);
    
    oscillator.start(audioContext.currentTime);
    oscillator.stop(audioContext.currentTime + 0.8);
  } catch (e) {
    console.log('Audio not supported');
  }
};

// Celebration confetti
const triggerCelebration = () => {
  confetti({
    particleCount: 100,
    spread: 70,
    origin: { y: 0.6 },
    colors: ['#f59e0b', '#10b981', '#3b82f6', '#8b5cf6']
  });
};

// Premium Company Logo Icon
const CompanyIcon = () => (
  <svg viewBox="0 0 40 40" className="w-10 h-10">
    <defs>
      <linearGradient id="logoGrad" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#fbbf24" />
        <stop offset="100%" stopColor="#d97706" />
      </linearGradient>
    </defs>
    <rect x="2" y="2" width="36" height="36" rx="8" fill="url(#logoGrad)" />
    <text x="20" y="28" textAnchor="middle" fill="white" fontSize="20" fontWeight="bold" fontFamily="Georgia">CI</text>
  </svg>
);

// Premium Clock Icon
const ClockIcon = () => (
  <svg viewBox="0 0 24 24" className="w-6 h-6" fill="none" stroke="currentColor" strokeWidth="2">
    <circle cx="12" cy="12" r="10" className="text-amber-400" />
    <polyline points="12,6 12,12 16,14" className="text-amber-400" />
  </svg>
);

// Premium Upward Trend Icon
const TrendUpIcon = () => (
  <svg viewBox="0 0 48 48" className="w-10 h-10 text-amber-400 opacity-60">
    <polyline points="4,36 16,24 24,32 44,12" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
    <polyline points="32,12 44,12 44,24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

// Premium Bullseye/Goal Icon
const GoalIcon = () => (
  <svg viewBox="0 0 48 48" className="w-10 h-10 text-emerald-400 opacity-60">
    <circle cx="24" cy="24" r="20" fill="none" stroke="currentColor" strokeWidth="2" />
    <circle cx="24" cy="24" r="14" fill="none" stroke="currentColor" strokeWidth="2" />
    <circle cx="24" cy="24" r="8" fill="none" stroke="currentColor" strokeWidth="2" />
    <circle cx="24" cy="24" r="3" fill="currentColor" />
  </svg>
);

// Premium Star Icon for Top Performer
const StarIcon = () => (
  <svg viewBox="0 0 24 24" className="w-8 h-8" fill="#fbbf24">
    <polygon points="12,2 15.09,8.26 22,9.27 17,14.14 18.18,21.02 12,17.77 5.82,21.02 7,14.14 2,9.27 8.91,8.26" />
  </svg>
);

// Premium Trophy Icon
const TrophyIcon = ({ className = "w-8 h-8" }) => (
  <svg viewBox="0 0 24 24" className={className} fill="none">
    <defs>
      <linearGradient id="trophyGold" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#fcd34d" />
        <stop offset="100%" stopColor="#f59e0b" />
      </linearGradient>
    </defs>
    <path d="M6 9H4a2 2 0 01-2-2V5a2 2 0 012-2h2M18 9h2a2 2 0 002-2V5a2 2 0 00-2-2h-2M6 3h12v6a6 6 0 01-12 0V3zM9 21h6M12 15v6" stroke="url(#trophyGold)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

// Warning/Alert Icon for Late Comers
const AlertIcon = ({ className = "w-8 h-8" }) => (
  <svg viewBox="0 0 24 24" className={className} fill="none">
    <path d="M12 9v4M12 17h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" 
      stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

// Megaphone/Broadcast Icon
const MegaphoneIcon = ({ className = "w-16 h-16" }) => (
  <svg viewBox="0 0 24 24" className={className} fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M3 11l18-5v12L3 13v-2z" />
    <path d="M11.6 16.8a3 3 0 11-5.8-1.6" />
  </svg>
);

// Premium Rank Badge Component
const RankBadge = ({ rank, size = "normal" }) => {
  const configs = {
    1: { 
      bg: "linear-gradient(135deg, #fcd34d 0%, #f59e0b 50%, #d97706 100%)",
      border: "#fcd34d",
      shadow: "0 4px 20px rgba(245, 158, 11, 0.5)",
      text: "1ST"
    },
    2: { 
      bg: "linear-gradient(135deg, #e5e7eb 0%, #9ca3af 50%, #6b7280 100%)",
      border: "#d1d5db",
      shadow: "0 4px 20px rgba(156, 163, 175, 0.5)",
      text: "2ND"
    },
    3: { 
      bg: "linear-gradient(135deg, #fdba74 0%, #f97316 50%, #c2410c 100%)",
      border: "#fdba74",
      shadow: "0 4px 20px rgba(249, 115, 22, 0.5)",
      text: "3RD"
    }
  };
  
  const config = configs[rank];
  const sizeClasses = size === "large" ? "w-24 h-24" : size === "medium" ? "w-20 h-20" : "w-16 h-16";
  const fontSize = size === "large" ? "text-2xl" : size === "medium" ? "text-xl" : "text-lg";
  
  return (
    <div 
      className={`${sizeClasses} rounded-full flex items-center justify-center`}
      style={{ 
        background: config.bg,
        border: `3px solid ${config.border}`,
        boxShadow: config.shadow
      }}
    >
      <span className={`${fontSize} font-bold text-white drop-shadow-lg`}>{config.text}</span>
    </div>
  );
};

// Users/Team Icon
const TeamIcon = () => (
  <svg viewBox="0 0 24 24" className="w-5 h-5 text-amber-400" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2" />
    <circle cx="9" cy="7" r="4" />
    <path d="M23 21v-2a4 4 0 00-3-3.87M16 3.13a4 4 0 010 7.75" />
  </svg>
);

// Lightning/Flash Icon for new sale
const FlashIcon = () => (
  <svg viewBox="0 0 24 24" className="w-24 h-24 text-amber-400" fill="currentColor">
    <polygon points="13,2 3,14 12,14 11,22 21,10 12,10" />
  </svg>
);

// Broadcast Popup Component
const BroadcastPopup = ({ broadcast, onClose, timeRemaining }) => {
  const priorityColors = {
    high: 'from-red-600 to-red-800 border-red-500',
    medium: 'from-amber-600 to-amber-800 border-amber-500',
    low: 'from-blue-600 to-blue-800 border-blue-500'
  };
  
  const colorClass = priorityColors[broadcast.priority] || priorityColors.medium;
  
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 animate-fadeIn">
      <div className={`bg-gradient-to-br ${colorClass} rounded-3xl p-8 max-w-3xl mx-8 border-4 shadow-2xl transform animate-scaleIn`}>
        <div className="flex items-start gap-6">
          <div className="flex-shrink-0">
            <MegaphoneIcon className="w-20 h-20 text-white" />
          </div>
          <div className="flex-1">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-4xl font-bold text-white">
                {broadcast.title}
              </h2>
              <div className="bg-white/20 rounded-full px-4 py-2">
                <span className="text-xl font-mono text-white">{timeRemaining}s</span>
              </div>
            </div>
            <p className="text-2xl text-white/90 leading-relaxed">
              {broadcast.message}
            </p>
            <div className="mt-6 flex items-center gap-2 text-white/60">
              <span className="text-sm">Admin Broadcast</span>
              <span className="text-sm">•</span>
              <span className="text-sm">Auto-closing in {timeRemaining} seconds</span>
            </div>
          </div>
        </div>
        {/* Progress bar */}
        <div className="mt-6 h-2 bg-white/20 rounded-full overflow-hidden">
          <div 
            className="h-full bg-white transition-all duration-1000 ease-linear"
            style={{ width: `${(timeRemaining / 60) * 100}%` }}
          />
        </div>
      </div>
    </div>
  );
};

const TVDisplay = () => {
  const [data, setData] = useState(null);
  const [currentView, setCurrentView] = useState('today'); // 'today', 'leaderboard', 'latecomers'
  const [loading, setLoading] = useState(true);
  const [lastTeamTotal, setLastTeamTotal] = useState(0);
  const [newSaleAgent, setNewSaleAgent] = useState(null);
  const [showBroadcast, setShowBroadcast] = useState(false);
  const [currentBroadcast, setCurrentBroadcast] = useState(null);
  const [broadcastTimeRemaining, setBroadcastTimeRemaining] = useState(60);
  const [lastBroadcastId, setLastBroadcastId] = useState(null);
  
  const viewTimerRef = useRef(null);
  const refreshTimerRef = useRef(null);
  const broadcastTimerRef = useRef(null);

  const fetchData = useCallback(async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/tv-display/data`);
      const newData = await response.json();
      
      // Check for new sales
      if (data && newData.today_sales.team_total > lastTeamTotal) {
        const previousSales = data.today_sales.agent_sales;
        const currentSales = newData.today_sales.agent_sales;
        
        for (const current of currentSales) {
          const previous = previousSales.find(p => p.email === current.email);
          if (previous && current.total > previous.total) {
            setNewSaleAgent(current.name);
            playBellSound();
            triggerCelebration();
            setTimeout(() => setNewSaleAgent(null), 5000);
            break;
          }
        }
      }
      
      // Check for new broadcast
      if (newData.active_broadcast && newData.active_broadcast.id !== lastBroadcastId) {
        setCurrentBroadcast(newData.active_broadcast);
        setLastBroadcastId(newData.active_broadcast.id);
        setShowBroadcast(true);
        setBroadcastTimeRemaining(60);
        playBellSound();
        
        // Start countdown timer
        if (broadcastTimerRef.current) clearInterval(broadcastTimerRef.current);
        broadcastTimerRef.current = setInterval(() => {
          setBroadcastTimeRemaining(prev => {
            if (prev <= 1) {
              clearInterval(broadcastTimerRef.current);
              setShowBroadcast(false);
              return 60;
            }
            return prev - 1;
          });
        }, 1000);
      }
      
      setLastTeamTotal(newData.today_sales.team_total);
      setData(newData);
      setLoading(false);
    } catch (error) {
      console.error('Failed to fetch TV display data:', error);
    }
  }, [data, lastTeamTotal, lastBroadcastId]);

  // Initial load and refresh every 30 seconds
  useEffect(() => {
    fetchData();
    refreshTimerRef.current = setInterval(fetchData, 5000);
    return () => {
      if (refreshTimerRef.current) clearInterval(refreshTimerRef.current);
      if (broadcastTimerRef.current) clearInterval(broadcastTimerRef.current);
    };
  }, []);

  // Update fetch interval when dependencies change
  useEffect(() => {
    if (refreshTimerRef.current) clearInterval(refreshTimerRef.current);
    refreshTimerRef.current = setInterval(fetchData, 5000);
    return () => {
      if (refreshTimerRef.current) clearInterval(refreshTimerRef.current);
    };
  }, [fetchData]);

  // View rotation logic
  // During 7 PM - 8 PM with late comers: Today (5 min) → Leaderboard (2 min) → Late Comers (1 min) → repeat
  // After 8 PM or no late comers: Today (5 min) → Leaderboard (2 min) → repeat
  useEffect(() => {
    if (!data) return;
    
    const isFirstHour = data.shift_info?.is_first_hour;
    const hasLateComers = data.late_comers?.length > 0;
    const hasNewJoinings = data.new_joinings?.length > 0;
    
    // Clear existing timer
    if (viewTimerRef.current) {
      clearTimeout(viewTimerRef.current);
    }
    
    // Set timer based on current view
    let duration;
    if (currentView === 'today') {
      duration = 5 * 60 * 1000; // 5 minutes for Today Sales
    } else if (currentView === 'leaderboard') {
      duration = 2 * 60 * 1000; // 2 minutes for Leaderboard
    } else if (currentView === 'latecomers') {
      duration = 1 * 60 * 1000; // 1 minute for Late Comers
    } else if (currentView === 'welcome') {
      duration = 2 * 60 * 1000; // 2 minutes for Welcome
    }
    
    viewTimerRef.current = setTimeout(() => {
      if (currentView === 'today') {
        setCurrentView('leaderboard');
      } else if (currentView === 'leaderboard') {
        if (isFirstHour && hasLateComers) {
          setCurrentView('latecomers');
        } else if (hasNewJoinings) {
          setCurrentView('welcome');
        } else {
          setCurrentView('today');
        }
      } else if (currentView === 'latecomers') {
        if (hasNewJoinings) {
          setCurrentView('welcome');
        } else {
          setCurrentView('today');
        }
      } else if (currentView === 'welcome') {
        setCurrentView('today');
      }
    }, duration);
    
    return () => {
      if (viewTimerRef.current) clearTimeout(viewTimerRef.current);
    };
  }, [currentView, data?.shift_info?.is_first_hour, data?.late_comers?.length, data?.new_joinings?.length]);

  useEffect(() => {
    const enterFullscreen = () => {
      const elem = document.documentElement;
      if (elem.requestFullscreen) {
        elem.requestFullscreen().catch(() => {});
      }
    };
    setTimeout(enterFullscreen, 1000);
  }, []);

  if (loading || !data) {
    return (
      <div className="h-screen w-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center" style={{ fontFamily: 'Georgia, serif' }}>
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-amber-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-xl text-slate-400">Loading Sales Dashboard...</p>
        </div>
      </div>
    );
  }

  const { today_sales, leaderboard, shift_info, late_comers, new_joinings } = data;
  const topPerformer = today_sales.agent_sales[0];
  const isFirstHour = shift_info?.is_first_hour;
  const hasLateComers = late_comers?.length > 0;
  const hasNewJoinings = new_joinings?.length > 0;

  return (
    <div className="h-screen w-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 p-4 overflow-hidden flex flex-col" style={{ fontFamily: 'Georgia, serif' }}>
      {/* Broadcast Popup */}
      {showBroadcast && currentBroadcast && (
        <BroadcastPopup 
          broadcast={currentBroadcast} 
          timeRemaining={broadcastTimeRemaining}
          onClose={() => setShowBroadcast(false)}
        />
      )}
      
      {/* New Sale Celebration Overlay */}
      {newSaleAgent && !showBroadcast && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60">
          <div className="text-center animate-pulse">
            <FlashIcon />
            <h1 className="text-5xl font-bold text-white mb-2">NEW SALE!</h1>
            <p className="text-3xl text-amber-400 font-semibold">{newSaleAgent}</p>
          </div>
        </div>
      )}

      {/* Header */}
      <header className="flex items-center justify-between mb-3 flex-shrink-0">
        <div className="flex items-center gap-3">
          <CompanyIcon />
          <div>
            <h1 className="text-2xl font-bold text-white tracking-tight">Creative Infotech</h1>
            <p className="text-sm text-slate-400">Sales Performance Dashboard</p>
          </div>
        </div>
        
        <div className="flex items-center gap-4">
          {/* Shift Timer */}
          <div className="bg-slate-800/80 rounded-xl px-5 py-3 border border-slate-700/50">
            <div className="flex items-center gap-3">
              <ClockIcon />
              <div>
                <div className="flex items-center gap-4">
                  <div>
                    <p className="text-2xl font-bold text-white font-mono">{shift_info.current_time_ist}</p>
                    <p className="text-xs text-slate-400 uppercase tracking-wider">IST</p>
                  </div>
                  <div className="w-px h-10 bg-slate-600"></div>
                  <div>
                    <p className="text-2xl font-bold text-blue-300 font-mono">{shift_info.current_time_est}</p>
                    <p className="text-xs text-slate-400 uppercase tracking-wider">ET</p>
                  </div>
                </div>
                <p className="text-sm text-amber-400 mt-1">{shift_info.time_remaining}</p>
              </div>
            </div>
            <div className="mt-2 h-1.5 bg-slate-700 rounded-full overflow-hidden">
              <div 
                className="h-full bg-gradient-to-r from-amber-500 to-emerald-500 transition-all duration-1000"
                style={{ width: `${shift_info.shift_progress}%` }}
              />
            </div>
          </div>

          {/* View Toggle */}
          <div className="flex gap-1 bg-slate-800/80 rounded-lg p-1 border border-slate-700/50">
            <button
              onClick={() => setCurrentView('today')}
              className={`px-4 py-2 rounded-lg font-medium text-sm transition-all ${
                currentView === 'today' 
                  ? 'bg-gradient-to-r from-amber-500 to-amber-600 text-white shadow-lg' 
                  : 'text-slate-400 hover:text-white'
              }`}
            >
              Today's Sales
            </button>
            <button
              onClick={() => setCurrentView('leaderboard')}
              className={`px-4 py-2 rounded-lg font-medium text-sm transition-all ${
                currentView === 'leaderboard' 
                  ? 'bg-gradient-to-r from-emerald-500 to-emerald-600 text-white shadow-lg' 
                  : 'text-slate-400 hover:text-white'
              }`}
            >
              Leaderboard
            </button>
            {isFirstHour && hasLateComers && (
              <button
                onClick={() => setCurrentView('latecomers')}
                className={`px-4 py-2 rounded-lg font-medium text-sm transition-all ${
                  currentView === 'latecomers' 
                    ? 'bg-gradient-to-r from-red-500 to-red-600 text-white shadow-lg' 
                    : 'text-slate-400 hover:text-white'
                }`}
              >
                Late Comers
              </button>
            )}
            {hasNewJoinings && (
              <button
                onClick={() => setCurrentView('welcome')}
                className={`px-4 py-2 rounded-lg font-medium text-sm transition-all ${
                  currentView === 'welcome' 
                    ? 'bg-gradient-to-r from-pink-500 to-purple-600 text-white shadow-lg' 
                    : 'text-slate-400 hover:text-white'
                }`}
              >
                New Joining
              </button>
            )}
          </div>
        </div>
      </header>

      {/* Main Content */}
      <div className="flex-1 overflow-hidden">
        {currentView === 'today' && (
          <TodaySalesView data={today_sales} topPerformer={topPerformer} />
        )}
        {currentView === 'leaderboard' && (
          <LeaderboardView data={leaderboard} />
        )}
        {currentView === 'latecomers' && (
          <LateComersView lateComers={late_comers} />
        )}
        {currentView === 'welcome' && (
          <NewJoiningWelcome joinings={new_joinings} />
        )}
      </div>
      
      {/* CSS for animations */}
      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes scaleIn {
          from { transform: scale(0.8); opacity: 0; }
          to { transform: scale(1); opacity: 1; }
        }
        .animate-fadeIn {
          animation: fadeIn 0.3s ease-out;
        }
        .animate-scaleIn {
          animation: scaleIn 0.3s ease-out;
        }
      `}</style>
    </div>
  );
};

// Mini Sparkline Component
const Sparkline = ({ data, width = 200, height = 40 }) => {
  if (!data || data.length === 0) return null;
  
  const maxVal = Math.max(...data.map(d => d.sales), 1);
  const points = data.map((d, i) => {
    const x = (i / (data.length - 1)) * width;
    const y = height - (d.sales / maxVal) * (height - 4);
    return `${x},${y}`;
  }).join(' ');
  
  const areaPoints = `0,${height} ${points} ${width},${height}`;
  
  return (
    <svg width={width} height={height} className="overflow-visible">
      <defs>
        <linearGradient id="sparkGrad" x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" stopColor="#10b981" stopOpacity="0.4" />
          <stop offset="100%" stopColor="#10b981" stopOpacity="0" />
        </linearGradient>
      </defs>
      <polygon points={areaPoints} fill="url(#sparkGrad)" />
      <polyline points={points} fill="none" stroke="#10b981" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      {/* Current point pulse */}
      {data.length > 0 && (() => {
        const lastIdx = data.length - 1;
        const cx = (lastIdx / (data.length - 1)) * width;
        const cy = height - (data[lastIdx].sales / maxVal) * (height - 4);
        return (
          <>
            <circle cx={cx} cy={cy} r="4" fill="#10b981" className="animate-pulse" />
            <circle cx={cx} cy={cy} r="2" fill="white" />
          </>
        );
      })()}
    </svg>
  );
};

// Challenge Ticker Component
const ChallengeTicker = ({ challenges }) => {
  const [currentIdx, setCurrentIdx] = useState(0);
  
  useEffect(() => {
    if (!challenges || challenges.length <= 1) return;
    const interval = setInterval(() => {
      setCurrentIdx(prev => (prev + 1) % challenges.length);
    }, 6000);
    return () => clearInterval(interval);
  }, [challenges]);
  
  if (!challenges || challenges.length === 0) return null;
  
  const challenge = challenges[currentIdx];
  const icons = {
    race: (
      <svg viewBox="0 0 24 24" className="w-6 h-6" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z" />
      </svg>
    ),
    target: (
      <svg viewBox="0 0 24 24" className="w-6 h-6" fill="none" stroke="currentColor" strokeWidth="2">
        <circle cx="12" cy="12" r="10" /><circle cx="12" cy="12" r="6" /><circle cx="12" cy="12" r="2" />
      </svg>
    ),
    fire: (
      <svg viewBox="0 0 24 24" className="w-6 h-6" fill="currentColor">
        <path d="M12 23c-3.866 0-7-3.134-7-7 0-3.037 1.956-5.644 3.5-7.5.75-.898 1.5-1.8 2-2.8.5 1 1.25 1.902 2 2.8 1.544 1.856 3.5 4.463 3.5 7.5 0 3.866-3.134 7-7 7zm0-14c-.5.5-1 1.3-1.5 2-.8.95-1.5 2.05-1.5 3.5 0 1.93 1.57 3.5 3.5 3.5s3.5-1.57 3.5-3.5c0-1.45-.7-2.55-1.5-3.5-.5-.7-1-1.5-1.5-2z"/>
      </svg>
    )
  };
  
  return (
    <div className="flex items-center gap-3 overflow-hidden">
      <div className="text-amber-400 animate-pulse flex-shrink-0">
        {icons[challenge.icon] || icons.fire}
      </div>
      <p className="text-base text-amber-200 font-medium truncate animate-fadeIn" key={currentIdx}>
        {challenge.message}
      </p>
    </div>
  );
};

// Today's Sales View
const TodaySalesView = ({ data, topPerformer }) => {
  const { date, agent_sales, team_total, team_target, offers, offer_totals, hourly_trend, challenges } = data;
  const targetPercent = team_target > 0 ? Math.min(100, Math.round((team_total / team_target) * 100)) : 0;

  return (
    <div className="h-full flex flex-col gap-3">
      {/* Top Row - Stats + Sparkline + Challenge */}
      <div className="flex gap-3 flex-shrink-0">
        {/* Team Total with Sparkline */}
        <div className="bg-gradient-to-br from-slate-800 to-slate-800/50 rounded-xl p-4 border border-amber-500/30 relative overflow-hidden flex-1">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-amber-400 font-semibold uppercase tracking-wider">Team Total</p>
              <p className="text-5xl font-bold text-white mt-1">{team_total}</p>
              <p className="text-xs text-slate-500 mt-1">{date}</p>
            </div>
            <div className="flex flex-col items-end gap-1">
              <p className="text-[10px] text-slate-500 uppercase tracking-wider">Hourly Trend</p>
              <Sparkline data={hourly_trend} width={140} height={36} />
            </div>
          </div>
        </div>

        {/* Team Target with Progress Ring */}
        <div className="bg-gradient-to-br from-slate-800 to-slate-800/50 rounded-xl p-4 border border-emerald-500/30 relative overflow-hidden w-56">
          <div className="flex items-center gap-4">
            <div className="relative w-16 h-16 flex-shrink-0">
              <svg viewBox="0 0 36 36" className="w-16 h-16 transform -rotate-90">
                <path d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                  fill="none" stroke="#334155" strokeWidth="3" />
                <path d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                  fill="none" stroke={targetPercent >= 100 ? '#10b981' : '#f59e0b'} strokeWidth="3"
                  strokeDasharray={`${targetPercent}, 100`}
                  strokeLinecap="round" className="transition-all duration-1000" />
              </svg>
              <div className="absolute inset-0 flex items-center justify-center">
                <span className={`text-sm font-bold ${targetPercent >= 100 ? 'text-emerald-400' : 'text-amber-400'}`}>
                  {targetPercent}%
                </span>
              </div>
            </div>
            <div>
              <p className="text-sm text-emerald-400 font-semibold uppercase tracking-wider">Target</p>
              <p className="text-3xl font-bold text-white">{team_target || '—'}</p>
            </div>
          </div>
        </div>

        {/* Top Performer */}
        <div className="bg-gradient-to-br from-slate-800 to-slate-800/50 rounded-xl p-4 border border-violet-500/30 flex-1">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 rounded-full bg-gradient-to-br from-amber-400 to-amber-600 flex items-center justify-center shadow-lg shadow-amber-500/30">
              <StarIcon />
            </div>
            <div className="flex-1">
              <p className="text-sm text-violet-400 font-semibold uppercase tracking-wider">Today's Top Performer</p>
              <p className="text-2xl font-bold text-white">{topPerformer?.name || '—'}</p>
            </div>
            <div className="text-right">
              <p className="text-4xl font-bold text-amber-400">{topPerformer?.total || 0}</p>
              <p className="text-xs text-slate-500">sales today</p>
            </div>
          </div>
        </div>
      </div>

      {/* Challenge Ticker Bar */}
      <div className="bg-gradient-to-r from-amber-900/40 via-amber-800/20 to-amber-900/40 rounded-lg px-4 py-2 border border-amber-500/20 flex-shrink-0">
        <ChallengeTicker challenges={challenges} />
      </div>

      {/* Agent Table */}
      <div className="flex-1 bg-slate-800/40 rounded-xl border border-slate-700/50 overflow-hidden flex flex-col">
        <div className="p-3 border-b border-slate-700/50 bg-slate-800/60 flex-shrink-0">
          <h2 className="text-lg font-bold text-white flex items-center gap-2">
            <TeamIcon />
            Agent Performance — {date}
          </h2>
        </div>
        
        <div className="flex-1 overflow-auto">
          <table className="w-full">
            <thead className="sticky top-0 bg-slate-800/95 backdrop-blur-sm">
              <tr>
                <th className="px-3 py-2 text-left text-sm font-semibold text-slate-400 w-16">Rank</th>
                <th className="px-3 py-2 text-left text-sm font-semibold text-slate-400">Agent</th>
                {offers.map(offer => (
                  <th key={offer.id} className="px-2 py-2 text-center text-xs font-semibold text-slate-500 whitespace-nowrap">
                    {offer.name.length > 12 ? offer.name.substring(0, 12) + '...' : offer.name}
                  </th>
                ))}
                <th className="px-3 py-2 text-center text-sm font-semibold text-emerald-400 w-20">Total</th>
                <th className="px-3 py-2 text-center text-sm font-semibold text-slate-400 w-20">Target</th>
              </tr>
            </thead>
            <tbody>
              {agent_sales.map((agent, index) => (
                <tr 
                  key={agent.email}
                  className={`border-b border-slate-700/20 transition-colors ${
                    index === 0 ? 'bg-amber-500/5' : 
                    index === 1 ? 'bg-slate-500/5' : 
                    index === 2 ? 'bg-orange-500/5' : 'hover:bg-slate-700/20'
                  }`}
                >
                  <td className="px-3 py-2">
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center text-sm font-bold ${
                      index === 0 ? 'bg-gradient-to-br from-amber-400 to-amber-600 text-white' :
                      index === 1 ? 'bg-gradient-to-br from-slate-300 to-slate-500 text-slate-800' :
                      index === 2 ? 'bg-gradient-to-br from-orange-400 to-orange-600 text-white' :
                      'bg-slate-700/50 text-slate-400'
                    }`}>
                      {index + 1}
                    </div>
                  </td>
                  <td className="px-3 py-2">
                    <span className={`text-base font-medium ${index < 3 ? 'text-white' : 'text-slate-300'}`}>
                      {agent.name}
                    </span>
                  </td>
                  {offers.map(offer => (
                    <td key={offer.id} className="px-2 py-2 text-center">
                      <span className={`text-sm ${agent.sales_by_offer[offer.id] > 0 ? 'text-emerald-400 font-semibold' : 'text-slate-600'}`}>
                        {agent.sales_by_offer[offer.id] || 0}
                      </span>
                    </td>
                  ))}
                  <td className="px-3 py-2 text-center">
                    <span className="text-lg font-bold text-emerald-400">{agent.total}</span>
                  </td>
                  <td className="px-3 py-2 text-center">
                    {agent.has_target ? (
                      <span className="text-sm text-slate-500">{agent.target}</span>
                    ) : (
                      <span className="text-slate-700">—</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot className="sticky bottom-0 bg-slate-800/95 backdrop-blur-sm">
              <tr>
                <td className="px-3 py-2" colSpan={2}>
                  <span className="text-base font-bold text-amber-400 uppercase tracking-wider">Team Total</span>
                </td>
                {offers.map(offer => (
                  <td key={offer.id} className="px-2 py-2 text-center">
                    <span className="text-sm font-semibold text-amber-300">{offer_totals[offer.id] || 0}</span>
                  </td>
                ))}
                <td className="px-3 py-2 text-center">
                  <span className="text-xl font-bold text-amber-400">{team_total}</span>
                </td>
                <td className="px-3 py-2 text-center">
                  <span className="text-base font-semibold text-slate-500">{team_target || '—'}</span>
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>
    </div>
  );
};

// Leaderboard View - World-Class Esports/Broadcast Style
const LeaderboardView = ({ data }) => {
  const { month_name, year, agents, total_agents } = data;
  
  const totalMonthlySales = agents.reduce((sum, a) => sum + a.monthly_sales, 0);
  const maxSales = agents[0]?.monthly_sales || 1;
  
  return (
    <div className="h-full flex flex-col relative overflow-hidden">
      {/* Animated Background Effects */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-amber-500/10 rounded-full blur-[100px] animate-pulse"></div>
        <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-blue-500/10 rounded-full blur-[100px] animate-pulse" style={{animationDelay: '1s'}}></div>
        <div className="absolute top-1/2 left-1/2 w-64 h-64 bg-purple-500/5 rounded-full blur-[80px] animate-pulse" style={{animationDelay: '2s'}}></div>
      </div>
      
      {/* Header Bar */}
      <div className="relative z-10 flex items-center justify-between mb-4 px-2">
        <div className="flex items-center gap-4">
          <div className="relative">
            <div className="w-14 h-14 bg-gradient-to-br from-amber-400 via-amber-500 to-orange-600 rounded-xl flex items-center justify-center shadow-lg shadow-amber-500/30">
              <svg viewBox="0 0 24 24" className="w-8 h-8 text-white" fill="currentColor">
                <path d="M12 1L9 9l-8 1 6 5-2 9 7-4 7 4-2-9 6-5-8-1-3-8z"/>
              </svg>
            </div>
            <div className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 rounded-full animate-ping"></div>
            <div className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 rounded-full flex items-center justify-center">
              <span className="text-[8px] font-bold text-white">LIVE</span>
            </div>
          </div>
          <div>
            <h1 className="text-3xl font-black text-transparent bg-clip-text bg-gradient-to-r from-amber-200 via-amber-400 to-amber-200 uppercase tracking-wider">
              {month_name} Champions League
            </h1>
            <p className="text-slate-400 text-sm">{total_agents} Elite Performers • {year} Season</p>
          </div>
        </div>
        
        <div className="flex items-center gap-6">
          <div className="text-right">
            <p className="text-slate-500 text-xs uppercase tracking-wider">Total Sales Generated</p>
            <p className="text-3xl font-black text-white">{totalMonthlySales} <span className="text-amber-400 text-lg">Sales</span></p>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex gap-4 relative z-10">
        
        {/* LEFT: Champion Throne */}
        {agents[0] && (
          <div className="w-80 flex flex-col">
            {/* The Champion Card */}
            <div className="flex-1 relative">
              {/* Glowing border effect */}
              <div className="absolute inset-0 bg-gradient-to-b from-amber-400 via-amber-500 to-amber-600 rounded-2xl blur-sm opacity-60"></div>
              <div className="absolute inset-[2px] bg-gradient-to-b from-slate-900 via-slate-800 to-slate-900 rounded-2xl"></div>
              
              <div className="relative h-full p-5 flex flex-col">
                {/* Crown Animation */}
                <div className="text-center mb-2">
                  <div className="inline-block animate-bounce" style={{animationDuration: '2s'}}>
                    <svg viewBox="0 0 24 24" className="w-12 h-12 text-amber-400 drop-shadow-[0_0_10px_rgba(251,191,36,0.5)]" fill="currentColor">
                      <path d="M5 16L3 5l5.5 5L12 4l3.5 6L21 5l-2 11H5z"/>
                      <path d="M19 19H5v2h14v-2z"/>
                    </svg>
                  </div>
                  <p className="text-amber-400 font-bold text-xs uppercase tracking-[0.3em] mt-1">Undisputed Champion</p>
                </div>
                
                {/* Champion Avatar */}
                <div className="flex-1 flex items-center justify-center">
                  <div className="relative">
                    <div className="absolute inset-0 bg-gradient-to-b from-amber-400 to-amber-600 rounded-full blur-xl opacity-40 scale-110 animate-pulse"></div>
                    <div className="relative w-36 h-36 rounded-full bg-gradient-to-br from-amber-300 via-amber-500 to-amber-700 p-1 shadow-2xl">
                      <div className="w-full h-full rounded-full bg-gradient-to-br from-slate-800 to-slate-900 flex items-center justify-center">
                        <span className="text-6xl font-black text-transparent bg-clip-text bg-gradient-to-b from-amber-200 to-amber-500">
                          {agents[0].name.charAt(0)}
                        </span>
                      </div>
                    </div>
                    <div className="absolute -bottom-2 left-1/2 -translate-x-1/2 bg-gradient-to-r from-amber-500 to-amber-600 px-4 py-1 rounded-full shadow-lg">
                      <span className="text-white font-bold text-sm">#1</span>
                    </div>
                  </div>
                </div>
                
                {/* Champion Name & Stats */}
                <div className="text-center mt-4">
                  <h2 className="text-2xl font-black text-white uppercase tracking-wide">{agents[0].name}</h2>
                  <div className="mt-4 grid grid-cols-2 gap-3">
                    <div className="bg-black/40 rounded-xl p-3 border border-amber-500/30">
                      <p className="text-amber-400 text-[10px] uppercase tracking-wider">Monthly</p>
                      <p className="text-3xl font-black text-white">{agents[0].monthly_sales}</p>
                    </div>
                    <div className="bg-black/40 rounded-xl p-3 border border-emerald-500/30">
                      <p className="text-emerald-400 text-[10px] uppercase tracking-wider">Today</p>
                      <p className="text-3xl font-black text-emerald-400">{agents[0].today_sales}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* RIGHT: Rankings Board */}
        <div className="flex-1 flex flex-col gap-3">
          {/* 2nd & 3rd Place */}
          <div className="grid grid-cols-2 gap-3">
            {/* 2nd Place */}
            {agents[1] && (
              <div className="relative group">
                <div className="absolute inset-0 bg-gradient-to-r from-slate-400 to-slate-500 rounded-xl blur-sm opacity-30 group-hover:opacity-50 transition-opacity"></div>
                <div className="relative bg-gradient-to-br from-slate-800/90 to-slate-900/90 rounded-xl p-4 border border-slate-500/30 backdrop-blur-sm">
                  <div className="flex items-center gap-4">
                    <div className="relative">
                      <div className="w-16 h-16 rounded-full bg-gradient-to-br from-slate-300 via-slate-400 to-slate-500 p-0.5">
                        <div className="w-full h-full rounded-full bg-slate-800 flex items-center justify-center">
                          <span className="text-2xl font-black text-slate-300">{agents[1].name.charAt(0)}</span>
                        </div>
                      </div>
                      <div className="absolute -bottom-1 -right-1 w-6 h-6 bg-gradient-to-br from-slate-300 to-slate-500 rounded-full flex items-center justify-center text-xs font-bold text-slate-800">2</div>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-white font-bold text-lg truncate">{agents[1].name}</p>
                      <div className="flex items-center gap-2 mt-1">
                        <span className="text-2xl font-black text-slate-300">{agents[1].monthly_sales}</span>
                        <span className="text-emerald-400 text-sm">+{agents[1].today_sales}</span>
                      </div>
                    </div>
                  </div>
                  {/* Progress to 1st */}
                  <div className="mt-3 h-1.5 bg-slate-700 rounded-full overflow-hidden">
                    <div className="h-full bg-gradient-to-r from-slate-400 to-slate-300 rounded-full" style={{width: `${(agents[1].monthly_sales / maxSales) * 100}%`}}></div>
                  </div>
                </div>
              </div>
            )}
            
            {/* 3rd Place */}
            {agents[2] && (
              <div className="relative group">
                <div className="absolute inset-0 bg-gradient-to-r from-orange-500 to-orange-600 rounded-xl blur-sm opacity-30 group-hover:opacity-50 transition-opacity"></div>
                <div className="relative bg-gradient-to-br from-slate-800/90 to-slate-900/90 rounded-xl p-4 border border-orange-500/30 backdrop-blur-sm">
                  <div className="flex items-center gap-4">
                    <div className="relative">
                      <div className="w-16 h-16 rounded-full bg-gradient-to-br from-orange-400 via-orange-500 to-orange-600 p-0.5">
                        <div className="w-full h-full rounded-full bg-slate-800 flex items-center justify-center">
                          <span className="text-2xl font-black text-orange-400">{agents[2].name.charAt(0)}</span>
                        </div>
                      </div>
                      <div className="absolute -bottom-1 -right-1 w-6 h-6 bg-gradient-to-br from-orange-400 to-orange-600 rounded-full flex items-center justify-center text-xs font-bold text-white">3</div>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-white font-bold text-lg truncate">{agents[2].name}</p>
                      <div className="flex items-center gap-2 mt-1">
                        <span className="text-2xl font-black text-orange-400">{agents[2].monthly_sales}</span>
                        <span className="text-emerald-400 text-sm">+{agents[2].today_sales}</span>
                      </div>
                    </div>
                  </div>
                  {/* Progress to 1st */}
                  <div className="mt-3 h-1.5 bg-slate-700 rounded-full overflow-hidden">
                    <div className="h-full bg-gradient-to-r from-orange-500 to-orange-400 rounded-full" style={{width: `${(agents[2].monthly_sales / maxSales) * 100}%`}}></div>
                  </div>
                </div>
              </div>
            )}
          </div>
          
          {/* Rankings List */}
          <div className="flex-1 bg-slate-900/60 rounded-xl border border-slate-700/50 backdrop-blur-sm overflow-hidden">
            <div className="h-full overflow-auto">
              {agents.slice(3).map((agent, index) => {
                const rank = index + 4;
                const progressWidth = (agent.monthly_sales / maxSales) * 100;
                const isHot = agent.today_sales >= 3;
                
                return (
                  <div 
                    key={agent.email}
                    className="flex items-center gap-4 px-4 py-3 border-b border-slate-800/50 hover:bg-slate-800/30 transition-colors group"
                  >
                    {/* Rank */}
                    <div className="w-10 h-10 rounded-lg bg-slate-800 flex items-center justify-center border border-slate-700 group-hover:border-slate-600 transition-colors">
                      <span className="text-lg font-bold text-slate-400">{rank}</span>
                    </div>
                    
                    {/* Avatar */}
                    <div className="relative">
                      <div className="w-12 h-12 rounded-full bg-gradient-to-br from-slate-600 to-slate-700 flex items-center justify-center">
                        <span className="text-lg font-bold text-slate-300">{agent.name.charAt(0)}</span>
                      </div>
                      {isHot && (
                        <div className="absolute -top-1 -right-1 w-5 h-5 bg-gradient-to-br from-orange-400 to-red-500 rounded-full flex items-center justify-center animate-pulse">
                          <span className="text-[10px]">🔥</span>
                        </div>
                      )}
                    </div>
                    
                    {/* Name & Progress */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between mb-1">
                        <p className="text-white font-semibold truncate">{agent.name}</p>
                        <div className="flex items-center gap-3">
                          <span className="text-xl font-bold text-white">{agent.monthly_sales}</span>
                          {agent.today_sales > 0 && (
                            <span className="text-sm font-semibold text-emerald-400 bg-emerald-500/20 px-2 py-0.5 rounded">
                              +{agent.today_sales}
                            </span>
                          )}
                        </div>
                      </div>
                      {/* Progress Bar */}
                      <div className="h-2 bg-slate-800 rounded-full overflow-hidden">
                        <div 
                          className="h-full rounded-full bg-gradient-to-r from-blue-500 via-purple-500 to-pink-500 transition-all duration-500"
                          style={{width: `${progressWidth}%`}}
                        ></div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
      
      {/* Bottom Stats Bar */}
      <div className="relative z-10 mt-3 grid grid-cols-4 gap-3">
        {agents.slice(0, 4).map((agent, index) => (
          <div key={agent.email} className="bg-slate-800/50 rounded-lg p-2 border border-slate-700/30 flex items-center gap-2">
            <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
              index === 0 ? 'bg-gradient-to-br from-amber-400 to-amber-600 text-white' :
              index === 1 ? 'bg-gradient-to-br from-slate-300 to-slate-500 text-slate-800' :
              index === 2 ? 'bg-gradient-to-br from-orange-400 to-orange-600 text-white' :
              'bg-slate-700 text-slate-400'
            }`}>
              {index + 1}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-xs text-slate-400 truncate">{agent.name}</p>
              <p className="text-sm font-bold text-white">{agent.monthly_sales}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// Late Comers View
const LateComersView = ({ lateComers }) => {
  return (
    <div className="h-full flex flex-col gap-4">
      {/* Title */}
      <div className="text-center flex-shrink-0">
        <div className="flex items-center justify-center gap-3">
          <AlertIcon className="w-12 h-12 text-red-500" />
          <h2 className="text-4xl font-bold text-red-400 tracking-tight">
            Today's Late Arrivals
          </h2>
          <AlertIcon className="w-12 h-12 text-red-500" />
        </div>
        <p className="text-lg text-slate-400 mt-2">Login Time: 7:00 PM</p>
      </div>

      {/* Late Comers List */}
      <div className="flex-1 flex items-center justify-center">
        {lateComers && lateComers.length > 0 ? (
          <div className="grid grid-cols-3 gap-6 max-w-5xl">
            {lateComers.map((person, index) => (
              <div 
                key={person.email}
                className="bg-gradient-to-br from-red-900/30 to-red-800/10 rounded-2xl p-6 border border-red-500/40 text-center transform hover:scale-105 transition-transform"
              >
                <div className="w-20 h-20 rounded-full bg-gradient-to-br from-red-500 to-red-700 flex items-center justify-center mx-auto mb-4 shadow-lg shadow-red-500/30">
                  <span className="text-3xl font-bold text-white">{person.name.charAt(0).toUpperCase()}</span>
                </div>
                <p className="text-2xl font-bold text-white mb-2">{person.name}</p>
                <div className="bg-red-500/20 rounded-lg py-2 px-4 inline-block">
                  <p className="text-xl font-mono text-red-400">Arrived: {person.arrival_time}</p>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center">
            <div className="w-24 h-24 rounded-full bg-emerald-500/20 flex items-center justify-center mx-auto mb-6">
              <svg viewBox="0 0 24 24" className="w-12 h-12 text-emerald-400" fill="none" stroke="currentColor" strokeWidth="2">
                <polyline points="20,6 9,17 4,12" />
              </svg>
            </div>
            <p className="text-3xl font-bold text-emerald-400">Everyone on Time!</p>
            <p className="text-xl text-slate-400 mt-2">Great job team!</p>
          </div>
        )}
      </div>
    </div>
  );
};


// New Joining Welcome View
const NewJoiningWelcome = ({ joinings }) => {
  const [currentIdx, setCurrentIdx] = useState(0);
  
  useEffect(() => {
    if (!joinings || joinings.length <= 1) return;
    const interval = setInterval(() => {
      setCurrentIdx(prev => (prev + 1) % joinings.length);
    }, 10000);
    return () => clearInterval(interval);
  }, [joinings]);
  
  if (!joinings || joinings.length === 0) return null;
  
  const person = joinings[currentIdx];
  
  return (
    <div className="h-full flex flex-col items-center justify-center relative overflow-hidden">
      {/* Animated Background */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-10 left-1/4 w-80 h-80 bg-purple-500/15 rounded-full blur-[120px] animate-pulse"></div>
        <div className="absolute bottom-10 right-1/4 w-80 h-80 bg-pink-500/15 rounded-full blur-[120px] animate-pulse" style={{animationDelay: '1s'}}></div>
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-amber-500/10 rounded-full blur-[100px] animate-pulse" style={{animationDelay: '0.5s'}}></div>
        {/* Confetti-like particles */}
        {[...Array(20)].map((_, i) => (
          <div
            key={i}
            className="absolute w-2 h-2 rounded-full"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              backgroundColor: ['#fbbf24', '#a855f7', '#ec4899', '#10b981', '#3b82f6'][i % 5],
              opacity: 0.3,
              animation: `float ${3 + Math.random() * 4}s ease-in-out infinite`,
              animationDelay: `${Math.random() * 3}s`
            }}
          />
        ))}
      </div>
      
      {/* Welcome Content */}
      <div className="relative z-10 text-center max-w-4xl">
        {/* Welcome Badge */}
        <div className="inline-flex items-center gap-2 bg-gradient-to-r from-purple-500/30 to-pink-500/30 rounded-full px-8 py-3 border border-purple-400/40 mb-8">
          <svg viewBox="0 0 24 24" className="w-6 h-6 text-purple-300" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2" />
            <circle cx="12" cy="7" r="4" />
            <line x1="12" y1="11" x2="12" y2="17" />
            <line x1="9" y1="14" x2="15" y2="14" />
          </svg>
          <span className="text-purple-200 font-semibold uppercase tracking-widest text-sm">New Team Member</span>
        </div>
        
        {/* Name with dramatic styling */}
        <div className="mb-6">
          <h1 className="text-7xl font-black text-transparent bg-clip-text bg-gradient-to-r from-amber-200 via-pink-300 to-purple-400 mb-4 leading-tight">
            Welcome
          </h1>
          
          {/* Avatar Circle */}
          <div className="relative inline-block mb-6">
            <div className="absolute inset-0 bg-gradient-to-r from-amber-400 via-pink-500 to-purple-600 rounded-full blur-xl opacity-50 scale-110 animate-pulse"></div>
            <div className="relative w-32 h-32 rounded-full bg-gradient-to-br from-amber-400 via-pink-500 to-purple-600 p-1 shadow-2xl mx-auto">
              <div className="w-full h-full rounded-full bg-slate-900 flex items-center justify-center">
                <span className="text-5xl font-black text-transparent bg-clip-text bg-gradient-to-b from-amber-200 to-pink-400">
                  {person.name.charAt(0).toUpperCase()}
                </span>
              </div>
            </div>
          </div>
          
          <h2 className="text-6xl font-black text-white mt-4 tracking-tight">
            {person.name}
          </h2>
          
          {person.designation && (
            <div className="mt-4 inline-flex items-center gap-2 bg-slate-800/80 rounded-full px-6 py-2 border border-slate-600/50">
              <svg viewBox="0 0 24 24" className="w-5 h-5 text-amber-400" fill="none" stroke="currentColor" strokeWidth="2">
                <rect x="2" y="7" width="20" height="14" rx="2" ry="2" />
                <path d="M16 21V5a2 2 0 00-2-2h-4a2 2 0 00-2 2v16" />
              </svg>
              <span className="text-xl text-amber-300 font-semibold">{person.designation}</span>
            </div>
          )}
        </div>
        
        {/* Welcome Message */}
        <div className="bg-gradient-to-br from-slate-800/60 to-slate-900/60 rounded-2xl p-8 border border-purple-500/30 backdrop-blur-sm mt-6 max-w-3xl mx-auto">
          <p className="text-2xl text-slate-200 leading-relaxed italic">
            "{person.message}"
          </p>
        </div>
        
        {/* Company signature */}
        <div className="mt-8 flex items-center justify-center gap-3">
          <div className="h-px w-16 bg-gradient-to-r from-transparent to-slate-500"></div>
          <span className="text-slate-400 text-sm font-semibold uppercase tracking-widest">Creative Infotech Family</span>
          <div className="h-px w-16 bg-gradient-to-l from-transparent to-slate-500"></div>
        </div>
        
        {/* Multiple joinings indicator */}
        {joinings.length > 1 && (
          <div className="mt-6 flex items-center justify-center gap-2">
            {joinings.map((_, i) => (
              <div key={i} className={`w-2 h-2 rounded-full transition-all ${i === currentIdx ? 'bg-purple-400 w-6' : 'bg-slate-600'}`} />
            ))}
          </div>
        )}
      </div>
      
      {/* CSS for floating animation */}
      <style>{`
        @keyframes float {
          0%, 100% { transform: translateY(0px) rotate(0deg); }
          50% { transform: translateY(-20px) rotate(180deg); }
        }
      `}</style>
    </div>
  );
};


export default TVDisplay;
